import type mapboxgl from "mapbox-gl";

export type MapSize = { width: number; height: number };
export type MapboxMapWithUpdate = mapboxgl.Map & { _update?: () => mapboxgl.Map };

const MAP_RESIZE_COMMIT_INTERVAL_MS = 20;
const MAP_RESIZE_COMMIT_THRESHOLD_PX = 10;
export const MAP_RESIZE_RENDER_FALLBACK_TIMEOUT_MS = 20;
const MAP_RESIZE_COMPENSATION_CLASS = "metx-map-resize-compensated";

export const getResizeCommitDelay = (pixelDelta: number, elapsedSinceLastCommit: number) => {
  if (pixelDelta >= MAP_RESIZE_COMMIT_THRESHOLD_PX) {
    return Math.max(0, MAP_RESIZE_COMMIT_INTERVAL_MS - elapsedSinceLastCommit);
  }

  // Small deltas are cheap to fake with CSS, so wait a full interval before committing.
  return MAP_RESIZE_COMMIT_INTERVAL_MS;
};

export const clearElementResizeCompensation = (element: HTMLElement) => {
  element.classList.remove(MAP_RESIZE_COMPENSATION_CLASS);
  element.style.removeProperty("--metx-map-rendered-width");
  element.style.removeProperty("--metx-map-rendered-height");
  element.style.removeProperty("--metx-map-scale-x");
  element.style.removeProperty("--metx-map-scale-y");
};

export const getRenderedMapSize = (map: mapboxgl.Map): MapSize => {
  const canvas = map.getCanvas();
  const width = Math.round(Number.parseFloat(canvas.style.width) || canvas.clientWidth);
  const height = Math.round(Number.parseFloat(canvas.style.height) || canvas.clientHeight);

  return { width, height };
};

export const getElementSize = (element: HTMLElement): MapSize => ({
  width: Math.round(element.clientWidth),
  height: Math.round(element.clientHeight),
});

export const isSameSize = (left: MapSize | null, right: MapSize | null) =>
  left?.width === right?.width && left?.height === right?.height;

export const applyElementResizeCompensation = (element: HTMLElement, renderedSize: MapSize, targetSize: MapSize) => {
  if (renderedSize.width <= 0 || renderedSize.height <= 0) {
    return;
  }

  const scaleX = targetSize.width / renderedSize.width;
  const scaleY = targetSize.height / renderedSize.height;

  if (scaleX === 1 && scaleY === 1) {
    clearElementResizeCompensation(element);
    return;
  }

  element.classList.add(MAP_RESIZE_COMPENSATION_CLASS);
  element.style.setProperty("--metx-map-rendered-width", `${renderedSize.width}px`);
  element.style.setProperty("--metx-map-rendered-height", `${renderedSize.height}px`);
  element.style.setProperty("--metx-map-scale-x", `${scaleX}`);
  element.style.setProperty("--metx-map-scale-y", `${scaleY}`);
};

export const clearCanvasResizeCompensation = (map: mapboxgl.Map) => {
  clearElementResizeCompensation(map.getCanvasContainer());
};

export const applyCanvasResizeCompensation = (map: mapboxgl.Map, renderedSize: MapSize, targetSize: MapSize) => {
  applyElementResizeCompensation(map.getCanvasContainer(), renderedSize, targetSize);
};
